import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleMarathonPrepInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "marathon-prep-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "best-routes": "Best Running Routes & Training Locations",
    "training-tips": "Essential Training Tips & Strategies",
    "local-regulations": "Local Regulations & Best Practices",
    recovery: "Recovery & Nutrition",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver – Running Routes",
    },
    {
      url: "https://bmovanmarathon.ca",
      label: "BMO Vancouver Marathon Official Website",
    },
    {
      url: "https://runvan.org",
      label: "RUNVAN (Vancouver International Marathon Society)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" must match the ref used in the corresponding <p ref={(el) => setSpotRef(..., el)}>
  // The conversation had 9 major location references, we'll provide 9 here with lat/lng from the article text.
  const locations = [
    {
      id: "stanley-park-seawall",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park Seawall",
    },
    {
      id: "false-creek-seawall",
      lat: 49.27000,
      lng: -123.12889,
      popupText: "False Creek Seawall",
    },
    {
      id: "pacific-spirit-park",
      lat: 49.25000,
      lng: -123.21000,
      popupText: "Pacific Spirit Park",
    },
    {
      id: "kits-to-jericho",
      lat: 49.27389,
      lng: -123.15511,
      popupText: "Kitsilano Beach to Jericho Beach",
    },
    {
      id: "grouse-grind",
      lat: 49.37120,
      lng: -123.09840,
      popupText: "Grouse Grind Trailhead",
    },
    {
      id: "queen-elizabeth-park",
      lat: 49.24172,
      lng: -123.11276,
      popupText: "Queen Elizabeth Park",
    },
    {
      id: "burnaby-lake-park",
      lat: 49.24243,
      lng: -122.94607,
      popupText: "Burnaby Lake Regional Park",
    },
    {
      id: "kerrisdale-track",
      lat: 49.23573,
      lng: -123.15267,
      popupText: "Kerrisdale Running Track",
    },
    {
      id: "hillcrest-centre",
      lat: 49.24408,
      lng: -123.10766,
      popupText: "Hillcrest Community Centre Gym",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // Mobile map reference
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Marathon Prep in Vancouver</title>
        <meta
          name="description"
          content="Discover the best running routes, training tips, local regulations, and recovery options for marathon preparation in Vancouver."
        />
        <meta
          name="keywords"
          content="marathon, vancouver, running, training, fitness, preparation"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/marathon-prep-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Marathon Prep in Vancouver"
        />
        <meta
          property="og:description"
          content="Discover the best routes, training tips, and local regulations for your marathon journey in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/marathon-prep-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/marathon-prep-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Marathon Prep in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Plan your next marathon in Vancouver with these essential running routes, tips, and resources."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/marathon-prep-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Runners along Vancouver's Seawall"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a runner’s dream—nestled between the Pacific Ocean
              and the North Shore Mountains, the city boasts a variety of
              breathtaking routes and a friendly running community. Whether
              you're gearing up for your very first marathon or looking to
              improve your personal best, this comprehensive guide will help you
              navigate the city's best running routes, essential training tips,
              and local regulations. Let's get you prepared to crush your next
              marathon in Vancouver!
            </p>

            {/* SECTION: Best Running Routes & Training Locations */}
            <h2 id="best-routes">Best Running Routes & Training Locations</h2>
            <p>
              From scenic waterfront paths to challenging mountain trails,
              Vancouver caters to every kind of runner. Below, you’ll find some
              top training spots, each with its own unique character.
            </p>

            {/* Stanley Park Seawall */}
            <h3>Stanley Park Seawall</h3>
            <p
              ref={(el) => setSpotRef("stanley-park-seawall", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park Seawall</strong>{" "}
              <Marker
                coordY={49.30000}
                coordX={-123.14000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – This 9–10 km paved loop encircling Stanley Park offers
              spectacular ocean and mountain views. The terrain is flat and
              stroller-friendly, ideal for tempo runs, long runs, or recovery
              jogs. With water fountains and washrooms along the way, it’s also
              convenient for longer distances.
            </p>

            {/* False Creek Seawall */}
            <h3>False Creek Seawall</h3>
            <p
              ref={(el) => setSpotRef("false-creek-seawall", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>False Creek Seawall</strong>{" "}
              <Marker
                coordY={49.27000}
                coordX={-123.12889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Running around False Creek covers about 9–10 km and offers city
              skyline views, passing by Granville Island, Science World, and
              Olympic Village. Mostly flat with a few small inclines at bridge
              approaches, it’s well-lit and perfect for evening runs.
            </p>

            {/* Pacific Spirit Park */}
            <h3>Pacific Spirit Park</h3>
            <p
              ref={(el) => setSpotRef("pacific-spirit-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Pacific Spirit Park</strong>{" "}
              <Marker
                coordY={49.25000}
                coordX={-123.21000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – These forest trails near UBC provide soft dirt and gravel
              surfaces, excellent for reducing the impact on joints. There are
              over 70 km of interconnected paths, offering various loops for all
              fitness levels.
            </p>

            {/* Kits to Jericho */}
            <h3>Kitsilano Beach to Jericho Beach</h3>
            <p
              ref={(el) => setSpotRef("kits-to-jericho", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kitsilano to Jericho</strong>{" "}
              <Marker
                coordY={49.27389}
                coordX={-123.15511}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – This 5 km route hugs the shoreline, offering spectacular views
              of the ocean and downtown skyline. The primarily flat path is
              suitable for steady runs, and the beach ambiance is great for
              a post-run cool-down.
            </p>

            {/* Grouse Grind */}
            <h3>Grouse Grind</h3>
            <p
              ref={(el) => setSpotRef("grouse-grind", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Grouse Grind Trailhead</strong>{" "}
              <Marker
                coordY={49.37120}
                coordX={-123.09840}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Known as “Mother Nature’s Stairmaster,” Grouse Grind is a short
              but intense 2.9 km climb with over 800 m of elevation gain. Great
              for building leg strength and cardiovascular endurance, but it's
              best for advanced runners or those seeking a rigorous challenge.
            </p>

            {/* Queen Elizabeth Park */}
            <h3>Queen Elizabeth Park</h3>
            <p
              ref={(el) => setSpotRef("queen-elizabeth-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Queen Elizabeth Park</strong>{" "}
              <Marker
                coordY={49.24172}
                coordX={-123.11276}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Featuring rolling hills and scenic pathways, this park is
              perfect for hill repeats or shorter loops. The view of downtown
              Vancouver from the top is a bonus treat.
            </p>

            {/* Burnaby Lake */}
            <h3>Burnaby Lake Regional Park</h3>
            <p
              ref={(el) => setSpotRef("burnaby-lake-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Burnaby Lake Park</strong>{" "}
              <Marker
                coordY={49.24243}
                coordX={-122.94607}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Just east of Vancouver, this park offers a 10 km loop around the
              lake on flat, well-groomed trails. It’s a serene environment
              perfect for long runs away from city traffic.
            </p>

            {/* Kerrisdale Track */}
            <h3>Kerrisdale Running Track</h3>
            <p
              ref={(el) => setSpotRef("kerrisdale-track", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kerrisdale Track</strong>{" "}
              <Marker
                coordY={49.23573}
                coordX={-123.15267}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A 400m rubberized track, ideal for interval training and speed
              workouts. The cushioned surface is gentle on joints, and the
              environment is great for precise pacing.
            </p>

            {/* Hillcrest Gym */}
            <h3>Hillcrest Community Centre Gym</h3>
            <p
              ref={(el) => setSpotRef("hillcrest-centre", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Hillcrest Centre</strong>{" "}
              <Marker
                coordY={49.24408}
                coordX={-123.10766}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Perfect for cross-training and treadmill runs on rainy days, the
              gym at Hillcrest offers modern cardio equipment, weights, and
              classes like yoga or Pilates to support your marathon training.
            </p>

            {/* SECTION: Essential Training Tips & Strategies */}
            <h2 id="training-tips">Essential Training Tips & Strategies</h2>
            <p>
              Marathon success in Vancouver (or anywhere) depends on a balanced
              training plan, consistency, and respect for your body’s limits.
            </p>
            <ul>
              <li>
                <strong>Gradual Mileage Build:</strong> Increase mileage by no
                more than 10% per week to reduce injury risk. Incorporate a
                weekly long run to develop endurance.
              </li>
              <li>
                <strong>Speedwork and Hills:</strong> Visit Kerrisdale Track for
                intervals, or tackle the hills in Queen Elizabeth Park for
                strength and explosive power.
              </li>
              <li>
                <strong>Rest and Recovery:</strong> Schedule rest days or
                low-impact cross-training (swimming, cycling). Proper sleep
                (7–9 hours) is essential for muscle repair.
              </li>
              <li>
                <strong>Join a Run Club:</strong> Vancouver is home to clubs
                like the Running Room, RUNVAN Club, or Mile2Marathon. They
                provide structured workouts and community support.
              </li>
            </ul>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              In Vancouver, city parks generally open from 6 AM to 10 PM.
              Runners should stick to sidewalks where available, face oncoming
              traffic when sidewalks are absent, and obey all traffic signals
              for safety. Be mindful of multi-use paths (like the seawall),
              staying in the pedestrian lanes and announcing yourself when
              passing. In wilder areas like Pacific Spirit Park, remain on
              marked trails, watch for wildlife, and follow posted advisories.
              For organized races, always follow volunteer and official
              instructions, pin your bib visibly, and stay aware of your
              surroundings for a smooth race-day experience.
            </p>

            {/* SECTION: Recovery & Nutrition */}
            <h2 id="recovery">Recovery & Nutrition</h2>
            <p>
              Proper recovery is just as crucial as the run itself. Vancouver
              offers excellent physiotherapy clinics, sports massage centers,
              and community centers with hot tubs and pools for post-run
              relaxation. Nutrition-wise, aim for a balanced intake of carbs,
              proteins, and healthy fats, plus adequate hydration—especially in
              summer. Sports nutrition shops like Body Energy Club can supply
              gels and electrolyte mixes, while local health-focused markets are
              great for fresh produce. If you need professional guidance,
              Vancouver has many sports dietitians experienced with marathon
              training.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. When is the best time to train for a Vancouver marathon?</h3>
              <p>
                You can train year-round, but winter is rainy with limited
                daylight. Most runners target spring or fall marathons when the
                temperatures are mild and scenic routes are less crowded.
              </p>

              <h3>2. Is Grouse Grind suitable for marathon training?</h3>
              <p>
                Grouse Grind is extremely steep and more of a hike, beneficial
                for hill strength training but not for daily runs. Integrate it
                carefully for leg strength and avoid overuse injuries.
              </p>

              <h3>3. Do I need permits to run in Vancouver’s parks?</h3>
              <p>
                Permits aren’t required for individual or small group runs. 
                However, organized events or large group gatherings may need a 
                permit from the City of Vancouver.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Preparing for a marathon in Vancouver is an incredible experience,
              combining fitness with breathtaking scenery. By leveraging the
              city’s diverse training routes, following a structured plan,
              respecting local guidelines, and focusing on proper recovery,
              you’ll set yourself up for a successful marathon finish. Lace up,
              get out there, and embrace the beauty of Vancouver—one run at a
              time.
            </p>
            <p>
              <strong>Disclaimer:</strong> This guide is for informational
              purposes. Always consult a healthcare professional before starting
              a new training program, and check official sources for the latest
              regulations or updates.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}